import { renderDraft } from '@fieldops/shared'
import { useEffect, useMemo, useRef, useState } from 'react'
import { cn } from '../../lib/cn.js'
import { Icon } from '../Icon.js'
import { Button } from '../ui.js'
import { AssistSheet } from './AssistSheet.js'
import { CheckSheet } from './CheckSheet.js'
import { DictationSheet } from './DictationSheet.js'
import { FieldCard } from './FieldCard.js'
import { askedFields, progressOf, shapeOf, stepIndexOf, stepProgress, stepsOf } from './model.js'
import { StationStep, type StationActions } from './StationStep.js'
import type { DraftState } from './useAnswerDraft.js'
import type {
  ReportAnswer,
  ReportAnswers,
  ReportDefinition,
  ReportField,
  ReportFinding,
  ReportHistory,
  ReportPhoto,
  ReportStationHistory,
  ReportStations,
  ReportSuggestion,
} from './types.js'

/**
 * The report filler.
 *
 * A surface that fills whatever it is given rather than a pane on the job card,
 * because it is worked through top to bottom on a phone with one hand. What it
 * is given is a popup over the job — a modal at a desk, a sheet on a phone —
 * so the job it belongs to is still there behind it. Decision 59.
 *
 * Density comes entirely from the `--control-*` tokens, so the office reads the
 * same component at a desk without a single breakpoint in this file. Decision 26.
 *
 * Presentational on purpose: answers, saving and submitting all arrive as props
 * so the whole surface can be rendered in a test against a definition and a set
 * of answers, with no server and no network.
 */

/** Names the dialog the filler is drawn in, from the form's own title. */
export const REPORT_TITLE_ID = 'report-title'

export type CaptureHandlers = {
  addPhoto: (fieldKey: string) => void
  removePhoto: (photoId: string) => void
  captionPhoto: (photoId: string, caption: string) => void
  sign: (fieldKey: string) => void
}

export function ReportFormSurface({
  title,
  subtitle,
  definition,
  answers,
  photosByField,
  findings,
  saveState,
  savePersisted = false,
  recovered = 0,
  readOnly,
  submitting,
  captureError,
  suggestions = [],
  suggestionsLoading = false,
  history,
  responseId,
  cloud,
  onChange,
  onChangeMany,
  onDictationOutcome,
  onComplete,
  onReopen,
  onDownload,
  downloading = false,
  downloadError,
  onExit,
  capture,
  stations = {},
  stationHistory = {},
  stationActions,
}: {
  title: string
  subtitle: string
  definition: ReportDefinition
  answers: ReportAnswers
  photosByField: Record<string, ReportPhoto[]>
  findings: ReportFinding[]
  saveState: DraftState
  /** Whether unsent answers are written to the device, not merely in memory. */
  savePersisted?: boolean
  /** Answers this session picked back up off the device, worth saying out loud. */
  recovered?: number
  readOnly: boolean
  submitting: boolean
  /** What a photo or a signature is doing, or what went wrong doing it. */
  captureError?: string | null
  /** Offers, never applied here. The sheet is the only way they reach a field. */
  suggestions?: ReportSuggestion[]
  suggestionsLoading?: boolean
  /** Earlier answers at this address, keyed by field key. */
  history?: ReportHistory
  /** The report a recording is sent against. Without one there is no microphone. */
  responseId?: string | null
  /** Whether a spoken section can be transcribed off-device, and what is left. */
  cloud?: { available: boolean; maxSeconds: number; remaining: number }
  onChange: (key: string, value: ReportAnswer) => void
  /** Accepting several suggestions at once, as one save. */
  onChangeMany?: (values: Record<string, ReportAnswer>) => void
  /** How many of a cloud pass's suggestions were kept. Best effort, never blocking. */
  onDictationOutcome?: (runId: string, accepted: number) => void
  onComplete: () => void
  onReopen?: () => void
  /** Opens the report's PDF — the document the customer is given. */
  onDownload?: () => void
  downloading?: boolean
  downloadError?: string | null
  /** The way back to the job. Left off where the popup draws one of its own. */
  onExit?: () => void
  capture?: CaptureHandlers
  /** The stations each station section asks about, keyed by section id. ADR 029. */
  stations?: ReportStations
  /** Each station's last check on another report. Shown, never filled in. */
  stationHistory?: ReportStationHistory
  /** Adding, renaming and retiring a station from the step it is checked on. */
  stationActions?: StationActions
}) {
  const steps = useMemo(() => stepsOf(definition), [definition])
  const [stepIndex, setStepIndex] = useState(0)
  const [blanksOnly, setBlanksOnly] = useState(false)
  const [checkOpen, setCheckOpen] = useState(false)
  const [assistOpen, setAssistOpen] = useState(false)
  const [dictateOpen, setDictateOpen] = useState(false)
  // The section a "same as last visit" card opened the sheet for, or null for all.
  const [assistScope, setAssistScope] = useState<string[] | null>(null)
  const [focusKey, setFocusKey] = useState<string | null>(null)

  // What dictation matched, held here rather than fetched: it came from words
  // spoken into this device and it goes to the same review sheet the server's
  // suggestions go to, so there is one place a suggestion can be accepted.
  const [heard, setHeard] = useState<ReportSuggestion[]>([])
  const [heardRun, setHeardRun] = useState<string | null>(null)
  const body = useRef<HTMLDivElement>(null)

  const step = steps[Math.min(stepIndex, steps.length - 1)]
  const progress = progressOf(definition, answers, stations)

  // The contradictions are what outline a card amber, so the card needs to know
  // whether its own key is named by one without re-deriving the rules.
  const flagged = useMemo(
    () =>
      new Set(
        findings.filter((finding) => finding.group === 'CONTRADICTION').map((f) => f.fieldKey),
      ),
    [findings],
  )
  const vague = useMemo(
    () => new Set(findings.filter((finding) => finding.group === 'VAGUE').map((f) => f.fieldKey)),
    [findings],
  )

  // Jumping from the check sheet lands on the field, not the top of its section.
  // A 28-field report has sections long enough that "go to the blank one" would
  // otherwise mean scrolling to find which of six cards was meant.
  useEffect(() => {
    if (focusKey === null) return
    const card = body.current?.querySelector<HTMLElement>(`[data-field="${focusKey}"]`)
    card?.scrollIntoView({ block: 'center' })
    card?.querySelector<HTMLElement>('input, textarea, select, button')?.focus()
    setFocusKey(null)
  }, [focusKey, stepIndex])

  if (!step) {
    return (
      <div className="grid h-full place-items-center bg-ink-100 p-6 text-center">
        <p className="max-w-prose text-sm text-ink-600">
          {title} has no questions on it yet, so there is nothing to fill in.
        </p>
      </div>
    )
  }

  const asked = askedFields(step, answers)
  const shown = blanksOnly
    ? asked.filter((field) => isBlankField(field, answers, photosByField))
    : asked
  const stationStep = step.kind === 'STATIONS'
  const lastStep = stepIndex >= steps.length - 1

  // Heard first: it is the newest thing the technician did, and it is the one
  // group they are waiting on an answer about.
  const offered = [...heard, ...suggestions.filter((s) => !heard.some((h) => h.fieldKey === s.fieldKey))]

  // Last visit's slow-changing answers that this step could take: the
  // building, the construction, the areas. One card per step, opening the same
  // review sheet as every other suggestion (decision 55), scoped to the step.
  const carriedHere = offered.filter(
    (suggestion) =>
      suggestion.source === 'carry' && step.fields.some((field) => field.key === suggestion.fieldKey),
  )
  const sheetSuggestions =
    assistScope === null ? offered : offered.filter((suggestion) => assistScope.includes(suggestion.fieldKey))

  return (
    <section
      aria-label="Report"
      className="relative flex h-full flex-col overflow-hidden bg-ink-100 text-ink-900"
    >
      <header className="shrink-0 bg-white shadow-[inset_0_-1px_0_var(--color-ink-200)]">
        <div className="flex items-center gap-3 px-[var(--page-pad)] pt-3 pb-2.5">
          {onExit ? (
            <button
              type="button"
              onClick={onExit}
              aria-label="Back to the job"
              className="grid size-8 shrink-0 place-items-center rounded-md text-ink-500 transition-colors hover:bg-ink-100 hover:text-ink-900"
            >
              <Icon name="back" />
            </button>
          ) : null}
          <div className="min-w-0 flex-1">
            <h1
              id={REPORT_TITLE_ID}
              className="truncate text-[15px] font-semibold tracking-[-0.01em]"
            >
              {title}
            </h1>
            <p className="truncate text-xs text-ink-500">{subtitle}</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span className="text-xs font-semibold text-ink-600 tabular-nums">
              {progress.answered} of {progress.total}
            </span>
            <SaveBadge state={saveState} persisted={savePersisted} readOnly={readOnly} />
          </div>
        </div>

        <div className="h-[3px] bg-ink-200">
          <div
            role="progressbar"
            aria-label="Answers filled in"
            aria-valuenow={progress.percent}
            aria-valuemin={0}
            aria-valuemax={100}
            className="h-[3px] bg-brand-800 transition-[width] duration-150"
            style={{ width: `${progress.percent}%` }}
          />
        </div>

        <nav
          aria-label="Sections"
          className="flex gap-1.5 overflow-x-auto px-[var(--page-pad)] py-2.5"
        >
          {steps.map((candidate, index) => {
            const counted = stepProgress(candidate, answers, stations)
            const active = index === stepIndex

            return (
              <button
                key={candidate.key}
                type="button"
                aria-current={active ? 'step' : undefined}
                onClick={() => setStepIndex(index)}
                className={cn(
                  'flex min-h-[var(--control-sm)] shrink-0 items-center gap-2 rounded-full pr-3 pl-2 text-[13px] font-medium transition-colors',
                  active
                    ? 'bg-brand-800 text-white ring-1 ring-brand-800 ring-inset'
                    : 'bg-white text-ink-700 ring-1 ring-ink-200 ring-inset hover:bg-ink-50',
                )}
              >
                <span
                  className={cn(
                    'inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10.5px] font-bold tabular-nums',
                    active
                      ? 'bg-white/20 text-white'
                      : counted.complete
                        ? 'bg-brand-100 text-brand-700'
                        : 'bg-ink-100 text-ink-500',
                  )}
                >
                  {counted.complete ? '✓' : `${counted.answered}/${counted.total}`}
                </span>
                {candidate.name}
              </button>
            )
          })}
        </nav>
      </header>

      <div
        ref={body}
        className="min-h-0 flex-1 overflow-y-auto px-[var(--page-pad)] pt-[var(--page-pad)] pb-24"
      >
        <div className="mx-auto flex w-full max-w-[760px] flex-col gap-2.5">
          {readOnly ? (
            <div className="flex flex-wrap items-center gap-3 rounded-xl bg-brand-50 px-3.5 py-3 ring-1 ring-brand-200 ring-inset">
              <p className="min-w-0 flex-1 text-[13px] text-brand-900">
                This report is complete. Reopen it to change an answer.
                {downloadError ? <span className="block text-ink-600">{downloadError}</span> : null}
              </p>
              {/* The document the customer gets, offered where the report says
                  it is finished — decision 78. Rendered at submission and served
                  from storage, so this is a download and not a re-render. */}
              {onDownload ? (
                <Button size="sm" loading={downloading} onClick={onDownload}>
                  Download PDF
                </Button>
              ) : null}
              {onReopen ? (
                <Button size="sm" onClick={onReopen}>
                  Reopen
                </Button>
              ) : null}
            </div>
          ) : null}

          {recovered > 0 ? (
            <p
              role="status"
              className="rounded-[10px] bg-brand-50 px-3 py-2 text-[12.5px] text-brand-900 ring-1 ring-brand-200 ring-inset"
            >
              {recovered === 1 ? 'One answer was' : `${recovered} answers were`} still waiting on
              this device and {recovered === 1 ? 'has' : 'have'} been put back on the report. Read{' '}
              {recovered === 1 ? 'it' : 'them'} back before you complete it.
            </p>
          ) : null}

          {captureError ? (
            <p
              role="status"
              className="rounded-[10px] bg-ink-50 px-3 py-2 text-[12.5px] text-ink-600 ring-1 ring-ink-200 ring-inset"
            >
              {captureError}
            </p>
          ) : null}

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              aria-pressed={blanksOnly}
              onClick={() => setBlanksOnly((on) => !on)}
              className={cn(
                'min-h-[var(--control-md)] rounded-full px-3.5 text-[13px] font-medium transition-colors',
                blanksOnly
                  ? 'bg-brand-800 text-white ring-1 ring-brand-800 ring-inset'
                  : 'bg-white text-ink-700 ring-1 ring-ink-200 ring-inset hover:bg-ink-50',
              )}
            >
              Blanks only
            </button>

            {/* Deliberately not a badge on the field itself. A suggestion beside
                every second question is an argument the technician has to win
                twenty-eight times; in a sheet it is one decision they choose to
                open. */}
            {!readOnly && onChangeMany && offered.length > 0 ? (
              <button
                type="button"
                onClick={() => {
                  setAssistScope(null)
                  setAssistOpen(true)
                }}
                className="min-h-[var(--control-md)] rounded-full bg-white px-3.5 text-[13px] font-medium text-ink-700 ring-1 ring-ink-200 ring-inset transition-colors hover:bg-ink-50"
              >
                {offered.length === 1 ? 'One suggestion' : `${offered.length} suggestions`}
              </button>
            ) : null}

            {/* Per section rather than per report. Twenty-eight questions in one
                breath is not how anybody talks, and the questions being on screen
                while you say them is what makes the matching work. */}
            {/* Not on a station step: twelve stations' answers in one breath
                is not how anybody talks, and every station would hear them. */}
            {!readOnly && onChangeMany && !stationStep ? (
              <button
                type="button"
                onClick={() => setDictateOpen(true)}
                className="ml-auto inline-flex min-h-[var(--control-md)] items-center gap-2 rounded-full bg-brand-600 px-4 text-[13px] font-semibold text-white transition-colors hover:bg-brand-700"
              >
                <Icon name="mic" className="size-4" />
                Talk to fill this section
              </button>
            ) : null}
          </div>

          <div className="flex items-baseline gap-2.5 px-0.5 pt-1.5">
            <span className="text-[11px] font-bold tracking-[0.06em] text-ink-400 uppercase">
              {step.name}
            </span>
            {step.hint ? <span className="text-xs text-ink-500">{step.hint}</span> : null}
          </div>

          {!readOnly && onChangeMany && carriedHere.length > 0 ? (
            <div className="flex flex-wrap items-center gap-3 rounded-[14px] bg-brand-50 px-3.5 py-3 ring-1 ring-brand-200 ring-inset">
              <Icon name="refresh" className="size-5 shrink-0 text-brand-800" />
              <div className="min-w-0 flex-1">
                <p className="text-[13.5px] font-semibold text-brand-900">Same as last visit?</p>
                <p className="text-[12px] text-brand-800">
                  {carriedHere.length === 1
                    ? 'One answer from the last report at this address.'
                    : `${carriedHere.length} answers from the last report at this address.`}{' '}
                  Check them before you keep them.
                </p>
              </div>
              <Button
                size="sm"
                onClick={() => {
                  setAssistScope(carriedHere.map((suggestion) => suggestion.fieldKey))
                  setAssistOpen(true)
                }}
              >
                Review
              </Button>
            </div>
          ) : null}

          {stationStep ? (
            <StationStep
              step={step}
              stations={stations[step.key] ?? []}
              history={stationHistory}
              answers={answers}
              photosByField={photosByField}
              blanksOnly={blanksOnly}
              readOnly={readOnly}
              jumpTo={focusKey}
              onChange={onChange}
              {...(capture
                ? {
                    capture: {
                      addPhoto: capture.addPhoto,
                      removePhoto: capture.removePhoto,
                      captionPhoto: capture.captionPhoto,
                    },
                  }
                : {})}
              {...(stationActions ? { actions: stationActions } : {})}
            />
          ) : null}

          {stationStep ? null : shown.map((field) => (
            <FieldCard
              key={field.id}
              field={field}
              answer={answers[field.key]}
              photos={photosByField[field.key] ?? []}
              flagged={flagged.has(field.key)}
              vague={vague.has(field.key)}
              draft={renderDraft(field.draftTemplate, answers)}
              history={history?.[field.key] ?? []}
              readOnly={readOnly}
              onChange={(value) => onChange(field.key, value)}
              {...(capture
                ? {
                    onAddPhoto: () => capture.addPhoto(field.key),
                    onRemovePhoto: capture.removePhoto,
                    onCaptionPhoto: capture.captionPhoto,
                    onSign: () => capture.sign(field.key),
                  }
                : {})}
            />
          ))}

          {!stationStep && shown.length === 0 ? (
            <div className="rounded-[14px] bg-white px-[var(--card-pad)] py-6 text-center ring-1 ring-ink-200 ring-inset">
              <p className="text-sm font-semibold">Nothing blank in this section.</p>
              <p className="mt-1 text-[13px] text-ink-500">
                Turn off the blanks filter to read it back.
              </p>
            </div>
          ) : null}
        </div>
      </div>

      <footer className="absolute inset-x-0 bottom-0 flex items-center gap-2.5 bg-white/95 px-[var(--page-pad)] py-2.5 shadow-[inset_0_1px_0_var(--color-ink-200)] backdrop-blur">
        <p className="min-w-0 flex-1 text-xs leading-snug text-ink-500">
          {footerHint(progress.answered, progress.total, saveState)}
        </p>
        <Button
          disabled={stepIndex === 0}
          onClick={() => setStepIndex((index) => Math.max(0, index - 1))}
          className="shrink-0 rounded-full"
        >
          Back
        </Button>
        <Button
          variant={lastStep ? 'primary' : 'secondary'}
          onClick={() =>
            lastStep ? setCheckOpen(true) : setStepIndex((index) => index + 1)
          }
          className="shrink-0 rounded-full"
        >
          {lastStep ? 'Check the report' : 'Next section'}
        </Button>
      </footer>

      <DictationSheet
        open={dictateOpen}
        onClose={() => setDictateOpen(false)}
        sectionName={step.name}
        sectionKey={step.sectionKey}
        fields={step.fields}
        answers={answers}
        responseId={responseId ?? null}
        {...(cloud ? { cloud } : {})}
        onOffer={(matched, runId) => {
          setHeard(matched)
          // Held so that whatever the technician does in the review sheet can be
          // reported back against the run that offered it. That figure is the
          // whole basis for deciding whether the cheaper model is good enough.
          setHeardRun(runId)
          setAssistOpen(true)
        }}
      />

      <AssistSheet
        open={assistOpen}
        onClose={() => setAssistOpen(false)}
        suggestions={sheetSuggestions}
        answers={answers}
        loading={suggestionsLoading}
        onApply={(accepted) => {
          if (!onChangeMany) return
          onChangeMany(
            Object.fromEntries(accepted.map((suggestion) => [suggestion.fieldKey, suggestion.value])),
          )
          // Once an offer has been decided on it stops being an offer. Leaving it
          // in the sheet would have the button still promising a suggestion for a
          // field the technician has just answered.
          setHeard((current) =>
            current.filter((item) => !accepted.some((one) => one.fieldKey === item.fieldKey)),
          )

          // Best effort, and only for a cloud pass: how many of its suggestions
          // survived review is the one number that says whether it was worth
          // paying for. A run the technician closed without deciding stays null
          // rather than being recorded as a zero.
          if (heardRun !== null) {
            onDictationOutcome?.(
              heardRun,
              accepted.filter((one) => one.source === 'heard').length,
            )
            setHeardRun(null)
          }
        }}
      />

      <CheckSheet
        open={checkOpen}
        onClose={() => setCheckOpen(false)}
        findings={findings}
        answered={progress.answered}
        total={progress.total}
        submitting={submitting}
        onJump={(fieldKey) => {
          setCheckOpen(false)
          setBlanksOnly(false)
          setStepIndex(stepIndexOf(steps, fieldKey))
          setFocusKey(fieldKey)
        }}
        onComplete={() => {
          setCheckOpen(false)
          onComplete()
        }}
      />
    </section>
  )
}

/**
 * Whether the technician still owes an answer here.
 *
 * A photo field is answered by having photos, which live beside the answers
 * rather than in them, so the blanks filter has to ask both. Only a photo
 * field: any field can carry photos now (decision 73), and photographing the
 * damage does not answer "what did you find".
 */
function isBlankField(
  field: ReportField,
  answers: ReportAnswers,
  photosByField: Record<string, ReportPhoto[]>,
): boolean {
  const key = field.key
  if (shapeOf(field) === 'photo' && (photosByField[key]?.length ?? 0) > 0) return false
  const value = answers[key]
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.trim().length === 0
  if (Array.isArray(value)) return value.length === 0
  return false
}

/**
 * The saved-state pill.
 *
 * The mockup put "Saved on device" here unconditionally, which would be a claim
 * the product cannot keep — a private window refuses to open IndexedDB at all.
 * So it says so exactly when it is true: the save has not landed, and the device
 * store took the answers. When even that failed the technician is told plainly.
 * Decisions 40 and 50.
 */
function SaveBadge({
  state,
  persisted,
  readOnly,
}: {
  state: DraftState
  persisted: boolean
  readOnly: boolean
}) {
  const { label, dot } = readOnly
    ? { label: 'Complete', dot: 'bg-brand-600' }
    : state === 'clean'
      ? { label: 'Saved', dot: 'bg-brand-600' }
      : persisted
        ? { label: 'Saved on device', dot: 'bg-ink-400' }
        : state === 'failed'
          ? { label: 'Not saved', dot: 'bg-red-600' }
          : { label: 'Saving', dot: 'bg-ink-400' }

  return (
    <span
      role="status"
      className="inline-flex h-6 items-center gap-1.5 rounded-full bg-ink-100 px-2.5 text-[11px] font-medium text-ink-600"
    >
      <span className={cn('size-1.5 rounded-full', dot)} />
      {label}
    </span>
  )
}

function footerHint(answered: number, total: number, state: DraftState): string {
  if (state === 'failed') return 'The last answer did not save. It goes again on the next change.'
  const left = total - answered
  if (left === 0) return 'Every field is filled in.'
  return `${left} ${left === 1 ? 'field' : 'fields'} still to fill in`
}
